import { findPageDescriptorByPath } from '@pm2/sitemap-utils';
import { loadNotebookLibrary } from '@utils/common';
import { countTextOccurrences } from './countTextOccurrences.js';
import { stringifyNotePlacementInfo } from './stringifyNotePlacementInfo.js';

async function createNoteFromSelection(sitemap, path, selection) {
  const gNotebook = await loadNotebookLibrary();
  const pageDescriptor = findPageDescriptorByPath(sitemap, path);
  const pageTitle = removeHtmlFormatting(pageDescriptor?.title ?? document.title);
  return gNotebook.createNote({
    context: {
      page: pageDescriptor ? {
        id: pageDescriptor.id,
        title: pageTitle
      } : undefined,
      placementInfo: stringifyNotePlacementInfo({
        path: path,
        ranges: extractNotePlacementRanges(selection)
      })
    },
    lockedContent: extractSelectionExcerpt(selection),
    title: pageTitle
  });
}

/**
 * extracts the text contents of the provided range and ensures that all katex
 * formulas are converted to raw LaTeX strings such that they can be rendered
 * properly in the Notebook context.
 */
function extractSelectionExcerpt(selection) {
  const fragment = selection.cloneContents();
  const katexs = fragment.querySelectorAll(".katex");
  for (const katex of katexs) {
    const annotation = katex.querySelector("annotation");
    if (!annotation) {
      continue;
    }
    katex.replaceWith(annotation);
    annotation.innerHTML = `\\(${annotation.innerHTML}\\)`;
  }
  return fragment.textContent?.trim();
}
function extractNotePlacementRanges(selection) {
  const elementIdInSelections = getElementIdsInSelection(selection);
  return elementIdInSelections.map((elementId, index) => {
    const element = document.querySelector(`[data-node-id="${elementId}"]`);
    const selectionWithinElement = document.createRange();
    if (index > 0) {
      // if this isn't the first element in the user's selection, then we'll
      // assume that his selection represents the first occurrence of the
      // selected text inside this element (as his selection clearly started
      // earlier on)
      //
      // therefore all we need to do, is to extract the actually selected text
      // inside this element
      //
      // to do so, create a range to extract the selected text inside this
      // element by moving the start of the range to this element, and cloning
      // the natural end of the user's selection
      selectionWithinElement.setStartBefore(element);
      selectionWithinElement.setEnd(selection.endContainer, selection.endOffset);

      // if the user's selection flows down into the following element, then
      // also limit the end of the range to only contain the text of this
      // element itself
      if (selectionWithinElement.cloneContents().querySelectorAll("[data-node-id]").length > 1) {
        selectionWithinElement.setEndAfter(element);
      }

      // now simply extract the selected text and let it represent the user's
      // selection for future reference
      return {
        elementId,
        occurence: 0,
        text: selectionWithinElement.toString()
      };
    }

    // if this IS the first selection made by the user, then we'll need to
    // determine how many occurrences of the same text there have been prior to
    // this occurence
    //
    // first of all, let's extract the part of the user's selection that's
    // contained inside this element
    selectionWithinElement.setStart(selection.startContainer, selection.startOffset);
    selectionWithinElement.setEnd(selection.endContainer, selection.endOffset);
    if (elementIdInSelections.length > 1) {
      // if the user's selection flows into the following element then limit the
      // selection to this element itself
      selectionWithinElement.setEndAfter(element);
    }
    const selectedText = selectionWithinElement.toString();

    // now proceed to create a range that consists of the text from the start of
    // the selected element until the start of the user's selection and count
    // the number of occurrences of the selected text
    const rangeBeforeSelection = document.createRange();
    rangeBeforeSelection.setStartBefore(element);
    rangeBeforeSelection.setEnd(selection.startContainer, selection.startOffset);
    const textBeforeSelection = rangeBeforeSelection.toString();

    // finally we have all the data that we need in order to extract the user's
    // selection inside this element
    return {
      elementId,
      occurence: countTextOccurrences(textBeforeSelection, selectedText),
      text: selectedText
    };
  });
}
function getElementIdsInSelection(selection) {
  // break the range up into multiple smaller ranges, which are all contained
  // within a single content element (this is very useful if the note spans
  // multiple elements, and some of these elements contain collapsible content,
  // as it will allow us to infer correct placement regardless of their state)
  const elementsInSelection = selection.cloneContents().querySelectorAll("[data-node-id]");
  if (elementsInSelection.length > 0) {
    return Array.from(elementsInSelection).map(it => it.getAttribute("data-node-id"));
  }

  // if the user's selection doesn't span multiple elements then simply find the
  // closest element parent node
  let commonAncestor = selection.commonAncestorContainer;
  while (commonAncestor) {
    if (isElementNode(commonAncestor) && commonAncestor.hasAttribute("data-node-id")) {
      return [commonAncestor.getAttribute("data-node-id")];
    }
    commonAncestor = commonAncestor.parentElement;
  }

  // if we get here, the selection couldn't be pointed back to an element in the
  // content - give up
  return [];
}
function isElementNode(node) {
  return node.nodeType === node.ELEMENT_NODE;
}

/**
 * remove html formatting and MathJax and decode special characters so the
 * string will be readable as plain text.
 */
function removeHtmlFormatting(str) {
  return str.replace(/\\\(.*\\\)/g, "").replace(/\\\[.*\\\]/g, "").replace(/<[^>]*>/g, "");
}

export { createNoteFromSelection, removeHtmlFormatting };
//# sourceMappingURL=createNoteFromSelection.js.map
